import {
    Edit,
    SimpleForm,
    TextField,
    SelectField,
    SelectInput,
    TextInput,
    required,
    maxLength,
    minLength,
    usePermissions,
    FunctionField,
    useRecordContext,
    TopToolbar,
    useTranslate,
    ReferenceInput,
    AutocompleteInput,
} from 'react-admin';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { SearchBar } from '@/components/global/list/searchBar.jsx';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { useDrawer } from '@/composables/useDrawer';
import { RightDrawer } from '@/components/global/drawers/RightDrawer';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput';
import { useTheme, useMediaQuery, Box } from '@mui/material';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton';
import { DrawerLink } from '@/components/global/drawers/DrawerLink.jsx';
import { softwareTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { ReferenceArrayFilter } from '@/components/global/filters/filters.jsx';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput.jsx';
import { softwareFilterToQuery } from '@/helpers/filters/filterToQuery.js';
import TagCategoryToolbar from '@/components/global/tags/TagCategoryToolbar.jsx';
import { useFormContext, useWatch } from 'react-hook-form';
import CustomDeleteButton from '@/components/global/buttons/CustomDeleteButton.jsx';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';

const getFilters = (isMobile, translate) => [
    <SearchBar key='search' source='value' labelKey='common.search' alwaysOn />,
    <CustomSelectArrayInput
        key='type'
        source='software->type_in'
        label={translate('resources.softwareAlias.filters.type')}
        choices={softwareTypeChoices}
        alwaysOn={isMobile}
        forceDesktopOnMobile={true}
    />,
    <ReferenceArrayFilter
        key='software'
        source='softwareId_in'
        reference='software'
        optionText={choice => `${choice.type} - ${choice.version}`}
        label={translate('resources.softwareAlias.filters.software')}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
];

const MoreActionsPopover = ({ permissions }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { openDrawer } = useDrawer();
    const canShow = canViewDetails('software-alias', permissions);

    if (!record || (!canShow && !permissions.isAdmin)) return null;

    return (
        <CustomMorePopover permissions={permissions} record={record}>
            {canShow && (
                <CustomShowModalButton label={translate('resources.softwareAlias.labels.show')} onOpen={() => openDrawer(record.id, 'software-alias-show')} />
            )}
            {permissions.isAdmin && (
                <>
                    <CustomEditModalButton
                        label={translate('resources.softwareAlias.labels.edit')}
                        onOpen={() => openDrawer(record.id, 'software-alias-edit')}
                    />
                    <CustomDeleteButton
                        resource='software-alias'
                        id={record.id}
                        label={translate('resources.softwareAlias.labels.delete')}
                        disabled={record?.isLinked}
                    />
                </>
            )}
        </CustomMorePopover>
    );
};

MoreActionsPopover.propTypes = {
    permissions: PropTypes.shape({
        isAdmin: PropTypes.bool,
        isBok: PropTypes.bool,
    }),
};

const ListActions = () => {
    const { isLoading, permissions } = usePermissions();
    const translate = useTranslate();
    if (isLoading) return null;

    return (
        <TopToolbar>
            <SearchFilterInput />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} />
            {permissions.isAdmin && (
                <CustomToolbarActionsPopover>
                    <CustomCreateButton label={translate('resources.softwareAlias.labels.create')} />
                </CustomToolbarActionsPopover>
            )}
        </TopToolbar>
    );
};

export const SoftwareAliasList = () => {
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const translate = useTranslate();
    const { openDrawer } = useDrawer();
    const filters = useMemo(() => getFilters(isMobile, translate), [isMobile, translate]);

    if (isLoading) return null;

    return (
        <CustomList filters={filters} filter={{ aliasType: 'alias', withAggregates: true }} actions={<ListActions />} addLastColumnShadow={true} empty={false}>
            <CustomDatagridConfigurable
                bulkActionButtons={false}
                rowClick={id => {
                    openDrawer(id, 'software-alias-show');
                    return false;
                }}
            >
                <TextField source='name' label={translate('resources.softwareAlias.fields.name')} />
                <SelectField source='software.type' choices={softwareTypeChoices} label={translate('resources.softwareAlias.fields.type')} />
                <FunctionField
                    source='softwareId'
                    sortBy='software.version'
                    label={translate('resources.softwareAlias.fields.software')}
                    render={record => (
                        <Box onClick={event => event.stopPropagation()}>
                            <DrawerLink resource='software' action='show' source={record.softwareId} label={record.software.version} relative />
                        </Box>
                    )}
                />
                <FunctionField
                    label={translate('common.options')}
                    render={() => (
                        <Box onClick={event => event.stopPropagation()}>
                            <MoreActionsPopover permissions={permissions} />
                        </Box>
                    )}
                />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};

export const SoftwareAliasCreate = ({ handleClose }) => {
    const translate = useTranslate();

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.softwareAlias.labels.create')}>
            <CustomCreate handleClose={handleClose}>
                <Form handleClose={handleClose} />
            </CustomCreate>
        </RightDrawer>
    );
};

export const SoftwareAliasEdit = ({ handleClose, elementId }) => {
    const translate = useTranslate();

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.softwareAlias.labels.edit')}>
            <Edit
                mutationMode='pessimistic'
                id={elementId}
                actions={false}
                redirect={resource => {
                    handleClose();
                    return resource;
                }}
            >
                <Form handleClose={handleClose} isEdit />
            </Edit>
        </RightDrawer>
    );
};

const Form = ({ handleClose, isEdit = false }) => {
    const translate = useTranslate();

    return (
        <SimpleForm toolbar={<TagCategoryToolbar onCancel={handleClose} cancelLabel={translate('common.cancel')} saveLabel={translate('common.save')} />}>
            <TextInput
                source='name'
                label={translate('resources.softwareAlias.fields.name')}
                validate={[
                    required(translate('common.validation.required')),
                    minLength(3, translate('common.validation.minLength', { count: 3 })),
                    maxLength(100, translate('common.validation.maxLength', { count: 100 })),
                ]}
                fullWidth
            />
            <TypeInput disabled={isEdit} />
            <SoftwareInput />
        </SimpleForm>
    );
};

Form.propTypes = {
    handleClose: PropTypes.func.isRequired,
    isEdit: PropTypes.bool,
};

const TypeInput = ({ disabled = false }) => {
    const translate = useTranslate();
    const { setValue } = useFormContext();

    const handleChange = () => {
        setValue('softwareId', null);
    };

    return (
        <SelectInput
            source='software.type'
            choices={softwareTypeChoices}
            onChange={handleChange}
            label={translate('resources.softwareAlias.fields.type')}
            validate={[required(translate('common.validation.required'))]}
            disabled={disabled}
            fullWidth
        />
    );
};

TypeInput.propTypes = {
    disabled: PropTypes.bool,
};

const SoftwareInput = () => {
    const translate = useTranslate();
    const type = useWatch({ name: 'software.type' });

    return (
        <Box className='autocomplete-parent'>
            <ReferenceInput source='softwareId' reference='software' filter={{ type }} enableGetChoices={() => !!type}>
                <AutocompleteInput
                    filterToQuery={softwareFilterToQuery}
                    validate={required(translate('common.validation.required'))}
                    readOnly={!type}
                    fullWidth={true}
                    disablePortal={true}
                    label={translate('resources.softwareAlias.fields.software')}
                />
            </ReferenceInput>
        </Box>
    );
};
